import ControlBase from '../fabric/ControlBase';
import { type fabric } from 'fabric';
import type CanvaSXBehaviors from '../objects/CanvaSXBehaviors';
import { type LinkToUrlBehaviorsInterface } from '../objects/LinkToUrlBehaviors';
import {
  ARROW_UP_RIGHT_FROM_SQUARE,
  RIGHT_ARROW,
  renderIcon,
} from '../objects/IconsHelper';
import { hasLinkToUrlBehaviors } from '../objects/guards';
import { sanitizeUrl } from '../utils/sanitizer';

type WhiteboardObject = fabric.Object &
  CanvaSXBehaviors &
  Partial<LinkToUrlBehaviorsInterface>;

const RADIUS = 12;
const PADDING = 6;
const HIDE_AT_ZOOM = 0.49;

class OpenUrlLinkControl extends ControlBase {
  actionName = 'openUrl';
  minDimension = 0; // Disable control hiding
  sizeX = 0;
  sizeY = 0;
  left = 0;
  top = 0;
  cursorStyle = 'pointer';
  hoveredControlObject?: WhiteboardObject;

  getVisibility(
    fabricObject: WhiteboardObject,
    persistentControlKey: string
  ): boolean {
    if (!hasLinkToUrlBehaviors(fabricObject) || !fabricObject.linkToUrl) {
      return false;
    }
    const zoom = fabricObject.canvas.getZoom();

    // Calculate minimum dimensions required for the control
    const minHeight = (RADIUS * 2 + PADDING) / fabricObject.scaleY / zoom;
    const minWidth = (RADIUS * 2 + PADDING) / fabricObject.scaleX / zoom;

    // Hide the control if the object is too small or zoom is too low
    if (
      zoom <= HIDE_AT_ZOOM ||
      fabricObject.height < minHeight ||
      fabricObject.width < minWidth
    ) {
      return false;
    }

    return super.getVisibility(fabricObject, persistentControlKey);
  }

  getMouseUpHandler(
    eventData: MouseEvent,
    fabricObject: WhiteboardObject
  ): fabric.ControlMouseEventHandler {
    return () => {
      const url = fabricObject.linkToUrl;

      if (!url) {
        return false;
      }

      const internalLink = this.getInternalLinkFromObject(fabricObject);

      if (internalLink?.hash) {
        // This is a pointer to an object on this whiteboard, so either zoom to
        // it or show a message if it's not found
        if (!fabricObject.controller.actionNavigate(internalLink?.hash)) {
          fabricObject.controller.actionShowMessage(
            'The linked object is not available on this whiteboard anymore.'
          );
          return false;
        }
        return true;
      }

      // If we didn't find a matching object, safely open the URL in a new window
      try {
        const parsedUrl = new URL(url);
        window.open(
          sanitizeUrl(parsedUrl.toString()),
          '_blank',
          'noopener,noreferrer'
        );
      } catch {
        console.warn('Invalid URL:', url);
      }

      return false;
    };
  }

  containsPoint(
    point: { x: number; y: number },
    fabricObject: WhiteboardObject
  ): boolean {
    const zoom = fabricObject.canvas.getZoom();

    const relativePoint = fabricObject.canvas.pointToCanvasCoords(point);

    const iconx =
      fabricObject.left +
      fabricObject.width * fabricObject.scaleX -
      (RADIUS + PADDING) / zoom;

    const icony = fabricObject.top + (RADIUS + PADDING) / zoom;

    return (
      Math.hypot(relativePoint.x - iconx, relativePoint.y - icony) <
      RADIUS / zoom
    );
  }

  onMouseOver(eventData: MouseEvent, fabricObject: WhiteboardObject): void {
    this.hoveredControlObject = fabricObject;

    // Show link popup using HoverController if it's an external link
    if (
      fabricObject.linkToUrl &&
      !this.getInternalLinkFromObject(fabricObject)
    ) {
      const zoom = fabricObject.canvas.getZoom();

      // Calculate position for the tooltip
      const middleOfIconX =
        fabricObject.left +
        fabricObject.width * fabricObject.scaleX -
        (RADIUS + PADDING) / zoom;
      const middleOfIconY = fabricObject.top + (RADIUS + PADDING) / zoom;

      // Create rect for the popup
      const rect = {
        left: middleOfIconX - RADIUS / zoom,
        top: middleOfIconY - RADIUS / zoom,
        width: (RADIUS * 2) / zoom,
        height: (RADIUS * 2) / zoom,
      };

      fabricObject.controller.actionLinkHovered(
        {
          rect,
          href: fabricObject.linkToUrl,
        },
        'top'
      );
    }
  }

  onMouseOut(eventData: MouseEvent, fabricObject: WhiteboardObject): void {
    this.hoveredControlObject = undefined;

    // Hide link popup
    if (fabricObject && fabricObject.controller) {
      fabricObject.controller.actionLinkHovered(undefined);
    }
  }

  render(
    ctx: CanvasRenderingContext2D,
    left: number,
    top: number,
    _styleOverride: any,
    fabricObject: WhiteboardObject
  ): void {
    const object = fabricObject;
    const zoom = object.canvas.getZoom();

    const alpha =
      this.hoveredControlObject === fabricObject
        ? 1
        : object.isHovered()
        ? 0.9
        : 0.7;
    const fillStyle = `rgba(255, 255, 255, ${alpha})`;

    ctx.save();
    ctx.beginPath();
    ctx.translate(left, top);
    ctx.translate(
      (object.width / 2) * object.scaleX * zoom - (RADIUS + PADDING),
      (-object.height / 2) * object.scaleY * zoom + (RADIUS + PADDING)
    );

    // Draw the red circle (no shadow)
    ctx.beginPath();
    ctx.arc(0, 0, RADIUS, 0, 2 * Math.PI);
    ctx.strokeStyle = '#e1e1e1';
    ctx.lineWidth = 2;

    // Now apply shadow *only for the stroke*
    ctx.shadowColor = `rgba(225, 225, 225, ${alpha})`; // '#e1e1e1';
    ctx.shadowBlur = 2;
    ctx.shadowOffsetX = 1;
    ctx.shadowOffsetY = 1;
    ctx.stroke();

    // Reset shadow
    ctx.shadowColor = 'transparent';
    ctx.shadowBlur = 0;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 0;

    // Add inner circle
    // ctx.strokeStyle = '#ffffff';
    ctx.fillStyle = fillStyle;
    ctx.fill();

    ctx.translate(-5, -5);
    ctx.scale(0.55, 0.55);
    renderIcon({
      ctx,
      icon: this.getInternalLinkFromObject(fabricObject)
        ? RIGHT_ARROW
        : ARROW_UP_RIGHT_FROM_SQUARE,
      fillStyle: 'rgb(51, 51, 51)', // --theme-primary-text
    });
    ctx.restore();
  }

  private getInternalLinkFromObject(fabricObject: WhiteboardObject) {
    if (hasLinkToUrlBehaviors(fabricObject) && fabricObject.linkToUrl) {
      const url = fabricObject.linkToUrl;
      const internalLink = fabricObject.controller.parseInternalLink(url);

      if (internalLink?.hash) {
        return internalLink;
      }
    }

    return null;
  }
}

export default OpenUrlLinkControl;
